import { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { fetchAdminUsers } from "@/services/adminUser.service";
import { sendAdminMessage } from "@/services/adminMessage.service";
import type { AdminUserOverview } from "@/types/adminUser.types";
import type { AdminUserMessageRequest } from "@/types/message.types";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

type MessageFormState = {
  recipientId: string;
  subject: string;
  message: string;
};

const SUBJECT_MAX_LENGTH = 255;
const MESSAGE_MAX_LENGTH = 2000;

export function UserMessageManagementPanel() {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const [users, setUsers] = useState<AdminUserOverview[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [formState, setFormState] = useState<MessageFormState>({
    recipientId: "",
    subject: "",
    message: "",
  });
  const subjectCount = formState.subject.length;
  const messageCount = formState.message.length;
  const isSubjectAtLimit = subjectCount >= SUBJECT_MAX_LENGTH;
  const isMessageAtLimit = messageCount >= MESSAGE_MAX_LENGTH;

  useEffect(() => {
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const data = await fetchAdminUsers();
        if (!cancelled) {
          setUsers(data);
        }
      } catch (error: any) {
        if (!cancelled) {
          setUsers([]);
        }
        toast.error(error?.response?.data?.message ?? t("common.genericError"));
      } finally {
        if (!cancelled) {
          setIsLoadingUsers(false);
        }
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSendMessage = async () => {
    const trimmedSubject = formState.subject.trim();
    const trimmedMessage = formState.message.trim();
    if (!formState.recipientId || !trimmedSubject || !trimmedMessage) {
      toast.error(t("admin.fillAllFields"));
      return;
    }
    setIsSending(true);
    const payload: AdminUserMessageRequest = {
      recipientId: formState.recipientId,
      subject: trimmedSubject,
      message: trimmedMessage,
    };
    try {
      await sendAdminMessage(payload);
      toast.success(t("admin.userMessageSent"));
      setFormState({
        recipientId: "",
        subject: "",
        message: "",
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card className="stat-card">
      <CardHeader>
        <CardTitle
          className="card-title-text flex items-center gap-2"
          style={primaryTextStyle}
        >
          <Mail className="w-5 h-5" style={iconStyle} />
          {t("admin.userMessagesTitle")}
        </CardTitle>
        <CardDescription
          className="card-description-text"
          style={secondaryTextStyle}
        >
          {t("admin.userMessagesDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label style={primaryTextStyle}>
              {t("admin.userMessagesRecipient")}
            </Label>
            <Select
              value={formState.recipientId}
              onValueChange={(value) =>
                setFormState((prev) => ({ ...prev, recipientId: value }))
              }
              disabled={isLoadingUsers || users.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("admin.userMessagesRecipientPlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.username} ({user.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="user-message-subject" style={primaryTextStyle}>
                {t("admin.userMessagesSubject")}
              </Label>
              <span
                className={isSubjectAtLimit ? "text-xs font-medium" : "text-xs"}
                style={isSubjectAtLimit ? primaryTextStyle : secondaryTextStyle}
              >
                {subjectCount}/{SUBJECT_MAX_LENGTH}
              </span>
            </div>
            <Input
              id="user-message-subject"
              value={formState.subject}
              maxLength={SUBJECT_MAX_LENGTH}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  subject: event.target.value,
                }))
              }
              placeholder={t("admin.userMessagesSubjectPlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="user-message-body" style={primaryTextStyle}>
                {t("admin.userMessagesBody")}
              </Label>
              <span
                className={isMessageAtLimit ? "text-xs font-medium" : "text-xs"}
                style={isMessageAtLimit ? primaryTextStyle : secondaryTextStyle}
              >
                {messageCount}/{MESSAGE_MAX_LENGTH}
              </span>
            </div>
            <Textarea
              id="user-message-body"
              value={formState.message}
              maxLength={MESSAGE_MAX_LENGTH}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  message: event.target.value,
                }))
              }
              placeholder={t("admin.userMessagesBodyPlaceholder")}
            />
          </div>

          <Button
            onClick={handleSendMessage}
            disabled={isSending}
            className="btn-primary gap-2"
          >
            <Send className="w-4 h-4" />
            {t("admin.userMessagesSend")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
